import { Box, Center, SegmentedControl } from "@mantine/core"
import {
	Button,
	DisplayText,
	Icon,
	Select,
	Spinner,
	Tooltip,
} from "@shopify/polaris"
import {
	AddProductMajor,
	ClockMajor,
	LinkMinor,
	PageDownMajor,
	ViewMajor,
} from "@shopify/polaris-icons"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { TopMenu } from "../components/TopMenu.jsx"
import { BackgroundJobsStateContext } from "../components/providers/BackgroundJobsStateProvider.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch.js"

const History = (props) => {
	const authenticatedFetch = useAuthenticatedFetch()
	const { state, dispatch } = useContext(GlobalStateContext)
	const { backgroundJobsState, setBackgroundJobsState } = useContext(
		BackgroundJobsStateContext,
	)
	const navigate = useNavigate()
	const { t } = useTranslation()

	const [tab, setTab] = useState("history")
	const [loading, setLoading] = useState(false)
	const [historyEntries, setHistoryEntries] = useState([])
	const [backgroundJobs, setBackgroundJobs] = useState([])

	const [sortBy, setSortBy] = useState("dateDesc")
	const [nextCursor, setNextCursor] = useState(null)

	const getHistoryEntries = (sortByValue, cursor) => {
		if (!cursor) setHistoryEntries([])
		setLoading(true)

		const params = new URLSearchParams({
			shop: state.shop,
			sortBy: sortByValue,
			...(cursor ? { cursor } : {}),
		})

		authenticatedFetch(`/api/firestore/getHistoryEntries?${params}`)
			.then((res) => res.json())
			.then((json) => {
				if (cursor) {
					setHistoryEntries((prev) => [
						...prev,
						...json.historyEntries,
					])
				} else {
					setHistoryEntries(json?.historyEntries || [])
				}
				setNextCursor(json?.nextCursor || null)
			})
			.catch((err) => console.error(err))
			.finally(() => setLoading(false))
	}

	const onSortByChange = (change) => {
		setSortBy(change)
		getHistoryEntries(change, null)
	}

	const loadMore = (sortByValue, cursor, tab) => {
		if (tab === "history") {
			getHistoryEntries(sortByValue, cursor)
		} else if (tab === "readyToImport") {
			getBackgroundJobs(cursor)
		}
	}

	const getBackgroundJobs = (cursor) => {
		if (!cursor) setBackgroundJobs([])
		setLoading(true)

		const cursorQueryString = cursor ? `&cursor=${cursor}` : ""

		authenticatedFetch(
			`/api/firestore/getBackgroundJobsHistory?shop=${state.shop}${cursorQueryString}`,
		)
			.then((res) => res.json())
			.then((json) => {
				if (cursor) {
					setBackgroundJobs((prev) => [
						...prev,
						...json.backgroundJobs,
					])
				} else {
					setBackgroundJobs(json?.backgroundJobs || [])
				}
				setNextCursor(json?.nextCursor || null)
			})
			.catch((err) => console.error(err))
			.finally(() => setLoading(false))
	}

	const importProduct = (productHash, product) => {
		const hasJobInStateAlready = backgroundJobsState.jobs.find(
			(job) => job.id === productHash,
		)

		if (!hasJobInStateAlready) {
			setBackgroundJobsState({
				jobs: [
					...backgroundJobsState.jobs,
					{
						id: productHash,
						product,
						status: "completed",
						dismissed: true,
					},
				],
			})
		}
		navigate(`/single?productHash=${productHash}`)
	}

	const createProductPage = (productHash, product) => {
		const hasJobInStateAlready = backgroundJobsState.jobs.find(
			(job) => job.id === productHash,
		)

		if (!hasJobInStateAlready) {
			setBackgroundJobsState({
				jobs: [
					...backgroundJobsState.jobs,
					{
						id: productHash,
						product,
						status: "completed",
						dismissed: true,
					},
				],
			})
		}
		navigate(`/pages?productHash=${productHash}`)
	}

	const retryBackgroundJob = (productHash) => {
		setBackgroundJobs(
			backgroundJobs.map((job) => {
				if (job.id === productHash) {
					return { ...job, status: "pending", dismissed: true }
				} else {
					return job
				}
			}),
		)

		authenticatedFetch("/api/firestore/retryBackgroundJob", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ productHash }),
		}).catch((err) => console.error(err))
	}

	useEffect(() => {
		setNextCursor(null)
		if (tab === "history") {
			getHistoryEntries(sortBy, null)
		} else if (tab === "readyToImport") {
			getBackgroundJobs(null)
		}
	}, [tab])

	const importModes = {
		single: t("history.single_import"),
		multiple: t("history.multiple_import"),
		crxSingle: t("history.single_import"),
		crxMultiple: t("history.multiple_import"),
		mcpSingle: t("history.single_import"),
		mcpMultiple: t("history.multiple_import"),
		pages: "Kopy Pages",
	}

	return (
		<div>
			<TopMenu goBack="/" storeSelector />

			<div
				style={{
					paddingTop: "5rem",
					width: "100%",
					display: "flex",
					justifyContent: "center",
				}}
			>
				<div
					style={{
						width: "100%",
						maxWidth: "40rem",
						padding: "2rem",
					}}
				>
					<div style={{ marginBottom: "2rem" }}>
						<SegmentedControl
							radius="xl"
							value={tab}
							onChange={setTab}
							data={[
								{
									value: "history",
									label: (
										<Center>
											<Icon
												source={ClockMajor}
												color="subdued"
											/>
											<Box ml={10}>
												{t("history.history")}
											</Box>
										</Center>
									),
								},
								{
									value: "readyToImport",
									label: (
										<Center>
											<Icon
												source={AddProductMajor}
												color="subdued"
											/>
											<Box ml={10}>
												{t("history.ready_import")}
											</Box>
										</Center>
									),
								},
							]}
						></SegmentedControl>
					</div>

					{tab === "history" ? (
						<>
							<div
								style={{
									display: "flex",
									alignItems: "flex-end",
									gap: "1rem",
								}}
							>
								<DisplayText size="large">
									{t("history.imported_products_history")}
								</DisplayText>
								<Tooltip
									content={
										<span style={{ padding: "1rem" }}>
											Export to CSV
										</span>
									}
								>
									<button
										style={{
											display: "flex",
											textDecoration: "none",
											background: "transparent",
											border: "none",
											cursor: "pointer",
											width: "100%",
											height: "100%",
										}}
										onClick={() =>
											exportHistory(
												authenticatedFetch,
												state.shop,
											)
										}
									>
										<Icon
											source={PageDownMajor}
											color="base"
										/>
									</button>
								</Tooltip>
							</div>

							<div style={{ marginTop: "2rem" }}>
								<Select
									label={t("history.sort_by")}
									options={[
										{
											label: t(
												"history.product_title_a_to_z",
											),
											value: "titleAsc",
										},
										{
											label: t(
												"history.product_title_z_to_a",
											),
											value: "titleDesc",
										},
										{
											label: t(
												"history.oldest_imported_first",
											),
											value: "dateAsc",
										},
										{
											label: t(
												"history.newest_imported_first",
											),
											value: "dateDesc",
										},
									]}
									value={sortBy}
									onChange={(change) =>
										onSortByChange(change)
									}
								/>
							</div>

							<div
								style={{
									marginTop: "2rem",
									display: "flex",
									flexDirection: "column",
									gap: "0.8rem",
								}}
							>
								{historyEntries?.length > 0
									? historyEntries.map((x) => {
											const productId =
												/\d+/.exec(x.productId)?.[0] ||
												""

											let originalProductUrl =
												x?.productUrl?.startsWith(
													"https://",
												) ||
												x?.productUrl?.startsWith(
													"http://",
												)
													? x.productUrl
													: `https://${x.productUrl}`

											return (
												<div
													key={x.id}
													className="shadow"
													style={{
														position: "relative",
														display: "flex",
														gap: "0.6rem",
														background: "#fff",
														borderRadius: "1rem",
														padding: "1rem",
														height: "8rem",
														border: "1px solid #ccc",
													}}
												>
													<div
														style={{
															position:
																"absolute",
															bottom: "0.5rem",
															right: "0.5rem",
														}}
													>
														<Tooltip
															content={
																<div
																	style={{
																		padding:
																			"0.2rem 0.6rem",
																	}}
																>
																	{t(
																		"history.imported_using",
																	)}{" "}
																	{
																		importModes[
																			x
																				.importMode
																		]
																	}{" "}
																	{x.importMode ===
																		"crxSingle" ||
																	x.importMode ===
																		"crxMultiple"
																		? t(
																				"history.from_the_kopy_chrome_extension",
																			)
																		: x.importMode ===
																					"mcpSingle" ||
																			  x.importMode ===
																					"mcpMultiple"
																			? t(
																					"history.from_the_kopy_mcp",
																				)
																			: null}
																</div>
															}
														>
															<div
																style={{
																	padding:
																		"0.2rem 0.6rem",
																	background:
																		"#e8e8e8",
																	borderRadius:
																		"1.5rem",
																	display:
																		"flex",
																	alignItems:
																		"center",
																	gap: "0.3rem",
																}}
															>
																{(x.importMode ===
																	"crxSingle" ||
																	x.importMode ===
																		"crxMultiple") && (
																	<svg
																		xmlns="http://www.w3.org/2000/svg"
																		width="0.8rem"
																		height="0.8rem"
																		viewBox="0 0 256 256"
																	>
																		<path
																			fill="#FFF"
																			d="M128.003 199.216c39.335 0 71.221-31.888 71.221-71.223c0-39.335-31.886-71.223-71.221-71.223c-39.335 0-71.222 31.888-71.222 71.223c0 39.335 31.887 71.223 71.222 71.223Z"
																		></path>
																		<path
																			fill="#229342"
																			d="M35.89 92.997c-5.313-9.203-11.558-18.862-18.736-28.977a127.98 127.98 0 0 0 110.857 191.981c11.78-16.523 19.78-28.437 23.996-35.74c8.099-14.028 18.573-34.112 31.423-60.251v-.015a63.993 63.993 0 0 1-110.857.017c-17.453-32.548-29.68-54.887-36.683-67.015Z"
																		></path>
																		<path
																			fill="#FBC116"
																			d="M128.008 255.996A127.972 127.972 0 0 0 256 127.997A127.983 127.983 0 0 0 238.837 64c-24.248-2.39-42.143-3.585-53.686-3.585c-13.088 0-32.139 1.195-57.152 3.585l-.014.01a63.993 63.993 0 0 1 55.444 31.987a63.993 63.993 0 0 1-.001 64.01l-55.42 95.989Z"
																		></path>
																		<path
																			fill="#1A73E8"
																			d="M128.003 178.677c27.984 0 50.669-22.685 50.669-50.67c0-27.986-22.685-50.67-50.67-50.67c-27.983 0-50.669 22.686-50.669 50.67s22.686 50.67 50.67 50.67Z"
																		></path>
																		<path
																			fill="#E33B2E"
																			d="M128.003 64.004H238.84a127.973 127.973 0 0 0-221.685.015l55.419 95.99l.015.008a63.993 63.993 0 0 1 55.415-96.014l-.002.001Z"
																		></path>
																	</svg>
																)}
																<span
																	style={{
																		fontSize:
																			"0.8rem",
																	}}
																>
																	{
																		importModes[
																			x
																				.importMode
																		]
																	}
																</span>
															</div>
														</Tooltip>
													</div>
													<div
														style={{
															width: "5rem",
															height: "5rem",
															display: "flex",
															flexDirection:
																"column",
															gap: "0.2rem",
														}}
													>
														<img
															style={{
																width: "5rem",
																height: "5rem",
																borderRadius:
																	"1rem",
																marginRight:
																	"1rem",
																objectFit:
																	"contain",
															}}
															alt=""
															src={
																x.productImageUrl
															}
															referrerPolicy="no-referrer"
														/>
													</div>
													<div
														style={{
															display: "flex",
															flexDirection:
																"column",
															gap: "0.3rem",
															flex: "1",
														}}
													>
														<div
															style={{
																display: "flex",
																justifyContent:
																	"space-between",
															}}
														>
															<p
																title={
																	x.productTitle
																}
																style={{
																	fontWeight:
																		"600",
																	fontSize:
																		"0.9rem",
																	color: "#333",
																	width: "18rem",
																	textOverflow:
																		"ellipsis",
																	overflow:
																		"hidden",
																	whiteSpace:
																		"nowrap",
																}}
															>
																{x.productTitle ||
																	"[Untitled]"}
															</p>
															<p
																style={{
																	color: "#999",
																	fontSize:
																		"0.8rem",
																}}
															>
																{new Date(
																	x.dateImported,
																).toLocaleString()}
															</p>
														</div>

														<div
															style={{
																display: "flex",
																gap: "0.3rem",
																alignItems:
																	"center",
															}}
														>
															<LinkMinor
																fill="#999"
																width="1rem"
																height="1rem"
															/>
															<a
																href={
																	originalProductUrl
																}
																title={
																	x.productUrl
																}
																target="_blank"
																rel="noreferrer"
																style={{
																	textDecoration:
																		"none",
																	width: "20rem",
																}}
															>
																<p
																	style={{
																		fontWeight:
																			"400",
																		fontSize:
																			"0.8rem",
																		color: "#999",
																		textOverflow:
																			"ellipsis",
																		overflow:
																			"hidden",
																		whiteSpace:
																			"nowrap",
																	}}
																>
																	{
																		x.productUrl
																	}
																</p>
															</a>
														</div>
														<a
															href={`https://${state.shop}/admin/products/${productId}`}
															target="_blank"
															rel="noreferrer"
															style={{
																textDecoration:
																	"none",
															}}
														>
															<div
																style={{
																	display:
																		"flex",
																	gap: "0.3rem",
																	alignItems:
																		"center",
																}}
															>
																<ViewMajor
																	fill="#999"
																	width="0.9rem"
																	height="0.9rem"
																/>
																<p
																	style={{
																		fontWeight:
																			"400",
																		fontSize:
																			"0.8rem",
																		color: "#999",
																	}}
																>
																	{t(
																		"history.view_product",
																	)}
																</p>
															</div>
														</a>
													</div>
												</div>
											)
										})
									: loading
										? ""
										: t("history.no_products_imported_yet")}
							</div>
						</>
					) : (
						""
					)}

					{tab === "readyToImport" ? (
						<>
							<DisplayText size="large">
								{t("history.ready_to_import")}
							</DisplayText>
							<div
								style={{
									marginTop: "2rem",
									display: "flex",
									flexDirection: "column",
									gap: "0.8rem",
								}}
							>
								{backgroundJobs?.length > 0
									? backgroundJobs.map((x) => {
											let originalProductUrl =
												x?.productUrl?.startsWith(
													"https://",
												) ||
												x?.productUrl?.startsWith(
													"http://",
												)
													? x.productUrl
													: `https://${x.productUrl}`
											const productImg =
												x?.product?.image?.src || null
											const productTitle =
												x?.product?.title || null
											const readyToBeImported =
												x?.status === "completed" &&
												productTitle !== null

											return (
												<div
													key={x.id}
													className="shadow"
													style={{
														position: "relative",
														display: "flex",
														gap: "0.6rem",
														background: "#fff",
														borderRadius: "1rem",
														padding: "1rem",
														border: "1px solid #ccc",
													}}
												>
													<div
														style={{
															width: "5rem",
															height: "5rem",
															display: "flex",
															flexDirection:
																"column",
															gap: "0.2rem",
														}}
													>
														{productImg ? (
															<img
																style={{
																	width: "5rem",
																	height: "5rem",
																	borderRadius:
																		"1rem",
																	marginRight:
																		"1rem",
																	objectFit:
																		"contain",
																}}
																alt=""
																src={productImg}
																referrerPolicy="no-referrer"
															/>
														) : (
															<svg
																style={{
																	width: "5rem",
																	height: "5rem",
																	color: "#555",
																}}
																viewBox="0 0 24 24"
															>
																<g fill="none">
																	<path
																		stroke="currentColor"
																		strokeWidth="2"
																		d="M3 11c0-3.771 0-5.657 1.172-6.828S7.229 3 11 3h2c3.771 0 5.657 0 6.828 1.172S21 7.229 21 11v2c0 3.771 0 5.657-1.172 6.828S16.771 21 13 21h-2c-3.771 0-5.657 0-6.828-1.172S3 16.771 3 13z"
																	></path>
																	<path
																		fill="currentColor"
																		fillRule="evenodd"
																		d="m19 13.585l-.02-.02c-.39-.39-.726-.726-1.026-.979c-.317-.267-.662-.502-1.088-.63a3 3 0 0 0-1.732 0c-.426.129-.77.363-1.088.63c-.3.253-.636.59-1.025.98l-.029.028c-.307.306-.487.486-.628.601l-.02.017l-.012-.023c-.087-.16-.189-.393-.36-.792l-.053-.124l-.022-.052c-.356-.83-.655-1.528-.95-2.054c-.305-.541-.685-1.05-1.277-1.346a3 3 0 0 0-1.597-.307c-.66.055-1.201.386-1.685.775c-.406.327-.86.77-1.388 1.297V13q0 .774.003 1.411l1.114-1.114c.69-.69 1.15-1.147 1.525-1.45c.37-.298.53-.335.6-.34a1 1 0 0 1 .532.102c.061.031.196.124.43.54c.236.42.493 1.016.877 1.912l.053.124l.017.038c.149.348.287.67.425.924c.145.265.355.583.709.802a2 2 0 0 0 1.398.27c.41-.073.723-.29.956-.482c.222-.184.47-.432.738-.7l.03-.03c.425-.425.701-.7.928-.891c.218-.184.32-.228.376-.245a1 1 0 0 1 .578 0c.056.017.158.061.376.245c.227.191.503.466.929.892l1.35 1.35c.046-.718.054-1.61.056-2.773"
																		clipRule="evenodd"
																	></path>
																	<circle
																		cx="16.5"
																		cy="7.5"
																		r="1.5"
																		fill="currentColor"
																	></circle>
																</g>
															</svg>
														)}
													</div>
													<div
														style={{
															display: "flex",
															flexDirection:
																"column",
															gap: "0.3rem",
															flex: "1",
														}}
													>
														<div
															style={{
																display: "flex",
																justifyContent:
																	"space-between",
															}}
														>
															{x?.status ===
															"pending" ? (
																<div
																	style={{
																		padding:
																			"0.1rem 0.4rem",
																		background:
																			"#cddef2",
																		color: "#0a478b",
																		borderRadius:
																			"1rem",
																		fontSize:
																			"0.75rem",
																		fontWeight:
																			"500",
																	}}
																>
																	PROCESSING
																</div>
															) : x?.status ===
															  "errored" ? (
																<div
																	style={{
																		padding:
																			"0.1rem 0.4rem",
																		background:
																			"#f4c9c9",
																		color: "#790a0a",
																		borderRadius:
																			"1rem",
																		fontSize:
																			"0.75rem",
																		fontWeight:
																			"500",
																	}}
																>
																	ERRORED
																</div>
															) : (
																<p
																	title={
																		productTitle
																	}
																	style={{
																		fontWeight:
																			"600",
																		fontSize:
																			"0.9rem",
																		color: "#333",
																		width: "18rem",
																		textOverflow:
																			"ellipsis",
																		overflow:
																			"hidden",
																		whiteSpace:
																			"nowrap",
																	}}
																>
																	{productTitle ||
																		"Processing..."}
																</p>
															)}
															<p
																style={{
																	color: "#999",
																	fontSize:
																		"0.8rem",
																}}
															>
																{new Date(
																	x.startedAt,
																).toLocaleString()}
															</p>
														</div>

														<div
															style={{
																display: "flex",
																gap: "0.3rem",
																alignItems:
																	"center",
																marginBottom:
																	"0.5rem",
															}}
														>
															<LinkMinor
																fill="#999"
																width="1rem"
																height="1rem"
															/>
															<a
																href={
																	originalProductUrl
																}
																title={
																	originalProductUrl
																}
																target="_blank"
																rel="noreferrer"
																style={{
																	textDecoration:
																		"none",
																	width: "20rem",
																}}
															>
																<p
																	style={{
																		fontWeight:
																			"400",
																		fontSize:
																			"0.8rem",
																		color: "#999",
																		textOverflow:
																			"ellipsis",
																		overflow:
																			"hidden",
																		whiteSpace:
																			"nowrap",
																	}}
																>
																	{
																		originalProductUrl
																	}
																</p>
															</a>
														</div>
														<div
															style={{
																display: "flex",
																gap: "0.3rem",
																justifyContent:
																	"flex-end",
															}}
														>
															{readyToBeImported && (
																<Button
																	onClick={() =>
																		importProduct(
																			x.id,
																			x.product,
																		)
																	}
																	size="slim"
																>
																	<div
																		style={{
																			height: "0.85rem",
																			width: "0.85rem",
																			display:
																				"inline-block",
																			marginRight:
																				"0.3rem",
																		}}
																	>
																		<Icon
																			source={
																				AddProductMajor
																			}
																			color="base"
																		/>
																	</div>
																	{t(
																		"single.import_product",
																	)}
																</Button>
															)}
															{readyToBeImported && (
																<Button
																	onClick={() =>
																		createProductPage(
																			x.id,
																			x.product,
																		)
																	}
																	size="slim"
																>
																	<svg
																		style={{
																			width: "0.85rem",
																			height: "0.85rem",
																			color: "#454F5B",
																			marginRight:
																				"0.3rem",
																		}}
																		viewBox="0 0 24 24"
																	>
																		<path
																			fill="currentColor"
																			d="M16.08 6.63c-.07-.1-.15-.19-.23-.27h-.01a2.85 2.85 0 0 0-1.11-.69l-1.38-.45c-.1-.04-.2-.11-.26-.2a.5.5 0 0 1-.1-.31a.5.5 0 0 1 .1-.31c.06-.09.15-.16.26-.2l1.38-.45c.42-.14.79-.38 1.1-.69c.29-.3.52-.67.67-1.07v-.03l.45-1.38c.04-.1.11-.2.2-.26a.5.5 0 0 1 .31-.1c.11 0 .22.04.31.1s.16.15.2.26l.45 1.38c.14.42.38.8.69 1.11c.31.32.69.55 1.11.69l1.38.45h.03c.1.04.19.11.26.2s.1.2.1.31a.5.5 0 0 1-.1.31c-.06.09-.15.16-.26.2l-1.38.45c-.42.14-.8.38-1.11.69c-.32.31-.55.69-.69 1.11l-.46 1.38v.03c-.04.09-.11.17-.19.23a.5.5 0 0 1-.31.1a.5.5 0 0 1-.31-.1a.52.52 0 0 1-.2-.26l-.45-1.38c-.1-.31-.25-.6-.45-.85m7.7 3.8l-.77-.24c-.24-.08-.45-.21-.62-.38c-.17-.18-.3-.39-.38-.62l-.25-.76a.33.33 0 0 0-.11-.15a.27.27 0 0 0-.34 0c-.05.04-.09.09-.11.15l-.25.76c-.07.23-.2.44-.37.61s-.38.3-.61.38l-.77.25c-.06.02-.11.06-.15.11a.27.27 0 0 0 0 .34c.04.05.09.09.15.11l.77.25c.23.07.45.21.62.38s.3.39.38.62l.25.76c.02.06.06.11.11.15a.27.27 0 0 0 .34 0c.05-.04.09-.09.11-.15l.25-.76c.08-.24.21-.45.38-.62c.18-.17.39-.3.62-.38l.77-.25c.06-.02.11-.06.15-.11s.05-.11.05-.17s-.01-.12-.05-.17s-.106-.08-.17-.11m-4.24 2.28l.26.8c.05.14.12.25.2.37v6.08c0 1.24-1.01 2.25-2.25 2.25H6.25C5.01 22.21 4 21.2 4 19.96V4.46c0-1.24 1.01-2.25 2.25-2.25h8.26c-.07.04-.15.08-.22.11l-1.42.46c-.4.15-.75.4-.99.75a2 2 0 0 0-.38 1.18c0 .43.13.84.37 1.18c.078.112.178.202.275.289l.045.041H7.75c-.41 0-.75.34-.75.75s.34.75.75.75h7.24l.035.058a1 1 0 0 1 .085.162l.46 1.41c.14.4.4.74.75.99c.35.24.76.37 1.18.37c0 .37.11.73.33 1.04s.52.53.91.67zm-11.79 5h8.5c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-8.5c-.41 0-.75.34-.75.75s.34.75.75.75m0-5h8.5c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-8.5c-.41 0-.75.34-.75.75s.34.75.75.75"
																		></path>
																	</svg>
																	{t(
																		"ai.create_product_page",
																	)}
																</Button>
															)}
															{x?.status ===
															"errored" ? (
																<Button
																	onClick={() =>
																		retryBackgroundJob(
																			x.id,
																		)
																	}
																>
																	{t(
																		"single.retry",
																	)}
																</Button>
															) : (
																""
															)}
														</div>
													</div>
												</div>
											)
										})
									: loading
										? ""
										: t(
												"history.no_products_to_be_imported_yet",
											)}
							</div>
						</>
					) : (
						""
					)}

					{loading && (
						<div
							style={{
								marginTop: "1rem",
								width: "100%",
								display: "flex",
								justifyContent: "center",
							}}
						>
							<Spinner size="large" />
						</div>
					)}

					{!loading && nextCursor && (
						<div style={{ marginTop: "1rem" }}>
							<Button
								fullWidth
								onClick={() =>
									loadMore(sortBy, nextCursor, tab)
								}
							>
								{t("common.load_more")}
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

async function exportHistory(authenticatedFetch, shop) {
	const params = new URLSearchParams({
		shop: shop,
	})
	authenticatedFetch(`/api/firestore/export-history?${params}`)
		.then((res) => res.blob())
		.then((blob) => {
			downloadFile(blob, "kopy-import-history.csv")
		})
}

function downloadFile(blob, name = "file.pdf") {
	const href = URL.createObjectURL(blob)
	const a = Object.assign(document.createElement("a"), {
		href,
		style: "display:none",
		download: name,
	})
	document.body.appendChild(a)
	a.click()
	URL.revokeObjectURL(href)
	a.remove()
}
export default History
